import { parseStoredNotes, type CrmNote } from "./coffre";
import { readPad, writePad } from "./NotePad";

export function isNotesFile(name: string): boolean {
  return name.toLowerCase() === "notes-crm.md";
}

export function notesToPad(raw: string): string | null {
  const notes = parseStoredNotes(raw);
  if (notes.length === 0) return null;
  const blocks = notes.flatMap((note) => {
    const part = readPad(note.body);
    const body = part.blocks?.length ? part.blocks : paragraphs(part.markdown);
    return [heading(note.heading), ...body];
  });
  return writePad("", blocks.map(withId));
}

export function padToNotes(previousRaw: string, pad: string): CrmNote[] {
  const previous = parseStoredNotes(previousRaw);
  const part = readPad(pad);
  if (!part.blocks?.length) return previous;
  const groups: { title: string; blocks: unknown[] }[] = [];
  part.blocks.forEach((block) => {
    if (isHeading(block)) {
      groups.push({ title: inlineText(block).trim() || "Note", blocks: [] });
      return;
    }
    if (groups.length === 0) groups.push({ title: "Note", blocks: [] });
    groups[groups.length - 1].blocks.push(block);
  });
  const used = new Set<number>();
  return groups.map((group, index) => {
    const prev = takeNote(previous, used, group.title, index);
    return {
      id: prev?.id || `note_${Math.random().toString(16).slice(2, 10)}`,
      heading: group.title,
      body: writePad("", group.blocks.length ? group.blocks : null).trim() || group.title,
      label: prev?.label || "",
      author: prev?.author || "",
      authorId: prev?.authorId,
      taskId: prev?.taskId,
      fromTask: prev?.fromTask,
    };
  });
}

function takeNote(notes: CrmNote[], used: Set<number>, title: string, index: number): CrmNote | undefined {
  const byTitle = notes.findIndex((note, at) => !used.has(at) && note.heading === title);
  if (byTitle >= 0) {
    used.add(byTitle);
    return notes[byTitle];
  }
  if (!used.has(index) && notes[index]) {
    used.add(index);
    return notes[index];
  }
  return undefined;
}

function heading(text: string): unknown {
  return {
    type: "heading",
    props: { level: 3, textColor: "default", backgroundColor: "default", textAlignment: "left", isToggleable: false },
    content: text ? [{ type: "text", text, styles: {} }] : [],
    children: [],
  };
}

function paragraphs(markdown: string): unknown[] {
  const text = markdown.trim();
  if (!text) return [];
  return text.split(/\n{2,}/).map((chunk) => ({
    type: "paragraph",
    props: { textColor: "default", backgroundColor: "default", textAlignment: "left" },
    content: [{ type: "text", text: chunk.replace(/\n/g, " "), styles: {} }],
    children: [],
  }));
}

function withId(block: unknown): unknown {
  if (!block || typeof block !== "object") return block;
  const row = block as { id?: string };
  if (row.id) return block;
  return { ...row, id: Math.random().toString(36).slice(2, 12) };
}

function isHeading(block: unknown): boolean {
  return Boolean(block && typeof block === "object" && (block as { type?: string }).type === "heading");
}

function inlineText(block: unknown): string {
  if (!block || typeof block !== "object") return "";
  const content = (block as { content?: unknown }).content;
  if (!Array.isArray(content)) return "";
  return content.map((item) => (item && typeof item === "object" && "text" in item ? String((item as { text: unknown }).text || "") : "")).join("");
}
